import { Injectable } from '@angular/core';
import { getFirestore, collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { COLLECTION_KNIGHTMEDS_INSTANCES } from 'src/app/global';

/**
 * A service for the knightmedsInstances collection -- the administration-
 * managed list of physical Knightmeds cabinets.
 *
 * Each cabinet gets one document, knightmedsInstances/{instanceId}, created
 * by an administrator. Its existence is what makes an instance id "legal":
 * a laptop opened with ?cabinet=<id> is only accepted once a matching
 * document is found (see knightmedsInstanceGuard).
 *
 * Note that the container writes in ContainersService target the
 * knightmedsInstances/{instanceId}/containers *subcollection*, and writing a
 * subcollection does not create its parent document. So a cabinet that was
 * never explicitly provisioned shows up in the Firebase console as an
 * id in italics but reports exists() === false here -- exactly the
 * distinction this service relies on.
 */
@Injectable({ providedIn: 'root' })
export class KnightmedsInstancesService {
  /** Whether the given id names a cabinet an administrator has provisioned */
  async exists(instanceId: string): Promise<boolean> {
    if (!isValidDocumentId(instanceId)) {
      return false;
    }
    const db = getFirestore();
    const snapshot = await getDoc(doc(db, COLLECTION_KNIGHTMEDS_INSTANCES, instanceId));
    return snapshot.exists();
  }

  /** The ids of every provisioned cabinet, sorted, for showing the user their options */
  async listInstanceIds(): Promise<string[]> {
    const db = getFirestore();
    const snapshot = await getDocs(collection(db, COLLECTION_KNIGHTMEDS_INSTANCES));
    return snapshot.docs.map(d => d.id).sort();
  }
}

/**
 * Whether a string is usable as a Firestore document id. Checked before
 * building a document reference because doc() *throws* on a malformed id
 * rather than returning a reference that reads as missing -- and the id here
 * comes from a URL a person typed, so malformed input is expected.
 */
function isValidDocumentId(instanceId: string): boolean {
  return instanceId.length > 0
    && !instanceId.includes('/')
    && instanceId !== '.'
    && instanceId !== '..';
}
